<template>
  <transition
    enter-active-class="duration-500 ease-out"
    enter-from-class="transform opacity-0"
    enter-to-class="transform opacity-100"
  >
    <div
      v-if="all_is_loaded"
      class="from-primary to-primary-700 dark:from-primary-900 dark:to-primary-700 h-full bg-gradient-to-b"
    >
      <TheMobileMenu />
      <div class="side-nav-gradient flex h-full overflow-hidden">
        <!-- BEGIN: Side Menu -->
        <div
          class="transition-width @container/menu relative hidden flex-col duration-300 ease-in-out md:flex print:hidden"
          :class="{
            'w-24 lg:w-64': !collapsedMenu,
            'w-24': collapsedMenu
          }"
        >
          <SeasonalEffect />
          <div class="z-10 min-h-0 flex-1 flex-col md:flex">
            <div class="shrink-0 px-5 py-3" style="max-width: 250px">
              <a href="" class="flex items-center justify-center gap-2">
                <img
                  :src="brand_logo"
                  class="inline-block @3xs/menu:w-1/3"
                  alt="logo-fm"
                />
                <img
                  v-if="!collapsedMenu"
                  :src="brand_text"
                  class="hidden @3xs/menu:inline-block @3xs/menu:w-2/3"
                  alt="logo-fm"
                />
              </a>
            </div>
            <nav
              class="flex-1 overflow-x-hidden overflow-y-auto px-5 pb-2 text-white dark:text-white"
            >
              <MainMenu :key="keyForRefreshMenu" />
            </nav>
            <div class="shrink-0 cursor-pointer py-1 pl-5 @3xs/menu:flex">
              <FontAwesomeIcon
                :icon="menuCollapseIcon"
                size="lg"
                class="text-white"
                @click="toggleCollapsed"
              />
            </div>
            <div class="shrink-0">
              <ReleaseNotificationComponent />
            </div>
            <div
              class="flex shrink-0 justify-center border-t p-4 @3xs/menu:justify-start"
              style="max-width: 250px"
            >
              <InfoUserModalComponent />
            </div>
          </div>
        </div>
        <!-- END: Side Menu -->
        <!-- BEGIN: Content -->
        <div
          class="bg-surface-base content flex flex-col"
          :class="{
            'rounded-r-none md:rounded-r-2xl': rightSideBar
          }"
        >
          <TheTopBar @ask-for-refresh="incrementKeyForRefresh" />
          <div class="min-h-0 flex-1 p-6">
            <RouterView :key="keyForRefresh" />
          </div>
        </div>
        <!-- END: Content -->
        <RightMenu v-if="rightSideBar" />
      </div>
    </div>
  </transition>
  <transition
    leave-active-class="duration-500 ease-out"
    leave-from-class="transform opacity-100"
    leave-to-class="transform opacity-0"
  >
    <div
      v-if="!all_is_loaded"
      id="loading-image"
      class="inset-0 flex h-screen w-screen flex-col items-center justify-center gap-5"
    >
      <img class="-intro-y" :src="brand_logo_full" width="250" />
      <div class="intro-y rounded-xl bg-white px-5 py-1.5 font-bold">
        {{ loadingMessage }}
      </div>
    </div>
  </transition>
  <FormInputConfigurator />
  <NotificationsContainer />
</template>

<script>
import fm_logo from '../../../assets/images/funeral-manager-logo-only.svg'
import fm_text from '../../../assets/images/funeral-manager-text-only.svg'
import fm_logo_full from '../../../assets/images/funeral-manager-full.svg'
import funero_logo from '../../../assets/images/funero-logo-only.svg'
import funero_text from '../../../assets/images/funero-text-only.svg'
import funero_logo_full from '../../../assets/images/funero-logo-full.svg'

import { mapActions, mapGetters, mapMutations } from 'vuex'
import NotificationsContainer from '@/views/layouts/main-layout/ToastsContainer.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import FormInputConfigurator from '@c/FormInputConfigurator.vue'
import i18n from '@/i18n'
import MainMenu from '@/views/layouts/main-layout/MainMenu.vue'
import TheMobileMenu from '@/views/layouts/mobile-menu/TheMobileMenu.vue'
import InfoUserModalComponent from '@/views/layouts/top-bar/InfoUserModalComponent.vue'
import TheTopBar from '@/views/layouts/top-bar/TheTopBar.vue'
import ReleaseNotificationComponent from '@/views/settings/versionNote/component/ReleaseNotificationComponent.vue'
import SeasonalEffect from '@/views/layouts/main-layout/SeasonalEffects/SeasonalEffect.vue'
import RightMenu from '@/views/layouts/main-layout/RightMenu.vue'

export default {
  components: {
    RightMenu,
    SeasonalEffect,
    ReleaseNotificationComponent,
    NotificationsContainer,
    FormInputConfigurator,
    TheTopBar,
    InfoUserModalComponent,
    FontAwesomeIcon,
    MainMenu,
    TheMobileMenu
  },
  data() {
    return {
      loadingMessage: '',
      keyForRefresh: 0,
      keyForRefreshMenu: 0
    }
  },
  computed: {
    ...mapGetters({
      collapsedMenu: 'sideMenu/collapsedMenu',
      getMetadataLoaded: 'metadata/getMetadataLoaded',
      getInitialMetadata: 'metadata/getInitialMetadata',
      functionalities: 'metadata/getFunctionalities',
      authUser: 'auth/getUser'
    }),
    rightSideBar() {
      return (
        this.$functionality.module.has('elys') ||
        this.$functionality.module.has('enaos')
      )
    },
    all_is_loaded() {
      return this.authUser !== null && this.getMetadataLoaded
    },
    brand_logo() {
      if (this.$brand === 'funero') {
        return funero_logo
      }
      return fm_logo
    },
    brand_text() {
      if (this.$brand === 'funero') {
        return funero_text
      }
      return fm_text
    },
    brand_logo_full() {
      if (this.$brand === 'funero') {
        return funero_logo_full
      }
      return fm_logo_full
    },
    menuCollapseIcon() {
      return [
        'fal',
        this.collapsedMenu ? 'chevron-double-right' : 'chevron-double-left'
      ]
    }
  },
  mounted() {
    document.body.classList.remove('error-page')
    document.body.classList.remove('login')
    document.body.classList.add('main')
    document.querySelectorAll('#app > div').forEach((element) => {
      element.classList.remove('content')
    })
    this.loadUser()
    this.emitter.on('reload-menu', () => {
      this.keyForRefreshMenu += 1
    })
    try {
      if (/(Mac|iPhone|iPod|iPad)/i.test(navigator.platform)) {
        document.documentElement.classList.add('os-macos')
      } else if (navigator.platform.indexOf('Win') !== -1) {
        document.body.classList.add('os-windows')
      }
    } catch (_) {}
  },
  methods: {
    ...mapActions('sideMenu', ['toggleCollapsed']),
    ...mapActions({
      fetchMetadata: 'metadata/fetchAllMetadata',
      processGetAuthUserAction: 'auth/getAuthUser'
    }),
    ...mapMutations({
      setPermissions: 'auth/setPermissions',
      setAuthUser: 'auth/setAuthUser'
    }),
    loadUser() {
      if (!localStorage.getItem('token')) {
        this.$router.push({ name: 'login' })
      }
      this.loadingMessage = this.$t('global.loading', 'loading')
      this.processGetAuthUserAction()
        .then((response) => {
          this.setAuthUser(response.user)
          const defaultView =
            response.user.settings?.default_view_preference || 'view'

          this.$store.commit('auth/setDisplayMode', defaultView)
          // localStorage.setItem(`view_pref_${response.user.id}`, defaultView)

          this.$h.setTheme(response.user.settings?.gui_settings?.theme)
          this.$h.setPattern(response.user.settings?.gui_settings?.pattern)
          this.$h.setPatternIntensity(
            response.user.settings?.gui_settings?.pattern_intensity
          )
          this.$h.setAvatarShape(
            response.user.settings?.gui_settings?.avatar_shape
          )

          this.loadPermission()
        })
        .catch((_reason) => {
          this.$router.push({ name: 'login' })
        })
    },
    loadPermission() {
      this.loadingMessage = this.$t('global.loading_permissions', 'loading')
      this.setPermissions()
      if (this.authUser.language !== this.$i18n.locale) {
        this.loadUserLocale()
      } else {
        this.loadMetadata()
      }
    },
    loadUserLocale() {
      this.loadingMessage = this.$t('global.loading_locale', 'loading')
      i18n.setLocale(this.authUser.language).then((_) => {
        this.loadMetadata()
      })
    },
    loadMetadata() {
      this.loadingMessage = this.$t('global.loading_data', 'loading')
      this.fetchMetadata()
    },
    incrementKeyForRefresh() {
      this.keyForRefresh += 1
    }
  }
}
</script>
